import { t } from "@lingui/macro";
import {
  ArrowLeftIcon,
  CircleNotchIcon,
  PencilSimpleIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";
import { Link } from "react-router";

import type { ExportStatus } from "../hooks/use-export-actions";

type MobileTab = "edit" | "preview";

/**
 * MobileReview — mobile read-only review view (Apply Edge builder v2).
 *
 * Presentational. On small screens the dense editor collapses to a focused,
 * scrollable preview with a calm action bar. Editing happens through the
 * `onEdit` affordance (the parent decides whether to open a sheet or route to
 * a focused editor). A simple Edit / Preview segmented control is included for
 * parents that DO allow inline mobile editing — pass `tab`/`onTabChange` to use
 * it, or omit for pure read-only.
 */
export type MobileReviewProps = {
  /** The artboard preview (iframe or scaled render). Wired by the parent. */
  previewSlot: ReactNode;
  resumeTitle: string;
  saveStatus?: "saved" | "saving" | "error";
  exportStatus?: ExportStatus;
  onEdit?: () => void;
  onRetry?: () => void;
  onExportClick?: () => void;
  /** Optional segmented control state for parents allowing mobile editing. */
  tab?: MobileTab;
  onTabChange?: (tab: MobileTab) => void;
  editSlot?: ReactNode;
};

const STATUS_LABEL: Record<NonNullable<MobileReviewProps["saveStatus"]>, () => string> = {
  saved: () => t`Saved`,
  saving: () => t`Saving…`,
  error: () => t`Save failed`,
};

export const MobileReview = ({
  previewSlot,
  resumeTitle,
  saveStatus = "saved",
  exportStatus = "idle",
  onEdit,
  onRetry,
  onExportClick,
  tab,
  onTabChange,
  editSlot,
}: MobileReviewProps) => {
  const showTabs = tab !== undefined && onTabChange !== undefined;
  const onPreview = !showTabs || tab === "preview";

  return (
    <div className="flex h-dvh h-screen min-h-0 flex-col bg-muted/40 lg:hidden">
      <div className="flex shrink-0 items-center gap-2 border-b border-border bg-background px-3 py-1">
        <Link
          to="/dashboard/resumes"
          aria-label={t`Back to resumes`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-foreground hover:bg-muted"
        >
          <ArrowLeftIcon size={20} />
        </Link>
        <span className="min-w-0 truncate font-medium text-foreground">{resumeTitle}</span>
      </div>
      {/* Status / segmented header */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-background/90 px-4 py-2 backdrop-blur">
        {showTabs ? (
          <div
            role="tablist"
            aria-label={t`Mobile mode`}
            className="flex rounded-full bg-secondary p-1"
          >
            {(["edit", "preview"] as const).map((value) => (
              <button
                key={value}
                role="tab"
                aria-selected={tab === value}
                className={cn(
                  "rounded-full px-4 py-1 text-xs font-medium transition-colors",
                  tab === value
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground",
                )}
                onClick={() => {
                  onTabChange(value);
                }}
              >
                {value === "edit" ? t`Edit` : t`Preview`}
              </button>
            ))}
          </div>
        ) : (
          <span className="font-mono text-[11px] text-muted-foreground">{t`Preview`}</span>
        )}
        <span
          className={cn(
            "font-mono text-[11px]",
            saveStatus === "error"
              ? "text-rose-500"
              : (saveStatus === "saving"
                ? "text-muted-foreground"
                : "text-emerald-600 dark:text-emerald-400"),
          )}
        >
          {STATUS_LABEL[saveStatus]()}
        </span>
        {saveStatus === "error" && onRetry && (
          <Button variant="ghost" size="sm" aria-label={t`Retry`} onClick={onRetry}>
            {t`Retry`}
          </Button>
        )}
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-hidden p-4">
        {onPreview ? (
          <div className="mx-auto h-full min-h-0 w-full max-w-[480px]">{previewSlot}</div>
        ) : (
          <div className="h-full overflow-y-auto">{editSlot}</div>
        )}
      </div>

      {/* Action bar */}
      <div className="flex shrink-0 items-center gap-2 border-t border-border bg-background p-3">
        {onEdit ? (
          <Button
            variant="outline"
            className="flex-1 gap-1.5"
            aria-label={t`Edit resume`}
            onClick={onEdit}
          >
            <PencilSimpleIcon size={16} />
            {t`Edit`}
          </Button>
        ) : null}
        <Button
          variant="primary"
          className="min-h-11 flex-1 gap-1.5"
          aria-label={t`Export resume`}
          disabled={exportStatus === "generating"}
          onClick={onExportClick}
        >
          {exportStatus === "generating" ? (
            <CircleNotchIcon size={16} className="animate-spin" />
          ) : (
            <UploadSimpleIcon size={16} />
          )}
          {exportStatus === "generating" ? t`Generating…` : t`Export`}
        </Button>
      </div>
    </div>
  );
};
